// src/components/contacts/EntityMultiPicker.tsx
//
// The searchable multi-select behind both relationship pickers — Companies on
// the contact form, Contacts on the company form. One combobox, so the two
// directions of the same relationship look and behave identically.
//
// It is deliberately presentation-only. It does not fetch, does not create
// records and knows nothing about capabilities: the wrapping picker
// (`CompanyMultiPicker`, `ContactMultiPicker`) owns the query, the inline
// "Create …" action and the permission that guards it. That keeps every
// network call and every authorization decision in one visible place per
// entity instead of behind a generic prop.
//
// ACCESSIBILITY — WAI-ARIA combobox with a listbox popup:
//   • the input owns `aria-activedescendant`; ArrowUp/ArrowDown move through
//     the options, Enter picks the active one, Escape closes the list;
//   • Enter on the input NEVER submits the surrounding form while the list is
//     open — picking a company must not accidentally create the contact;
//   • a polite live region announces the result count, including "none";
//   • each selected item has its own labelled remove button, so selections can
//     be removed from the keyboard without a mouse.
//
// LAYOUT — the option list scrolls inside a capped height and selected items
// wrap, so a long selection or a long result list cannot push the parent
// modal's Cancel / Create buttons out of reach on a small screen.

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

export interface PickerItem {
  id: string;
  /** Main line — a company or person name. */
  label: string;
  /**
   * Disambiguator shown beside the label — a domain or an email. Two "Jane
   * Smith"s are only distinguishable by it, so it is part of the accessible
   * name too.
   */
  detail?: string | null;
}

export interface PickerCreateAction {
  /** Visible text, e.g. `Create “Acme”`. */
  label: string;
  onCreate: () => void;
  isCreating?: boolean;
}

interface Props {
  /** Visible label; also names the input. */
  label: string;
  /** Marks the label as required, like the form's other required fields. */
  required?: boolean;
  placeholder?: string;
  query: string;
  onQueryChange: (q: string) => void;
  /** Search results, already scoped to the workspace by the server. */
  options: PickerItem[];
  isFetching?: boolean;
  selected: PickerItem[];
  onAdd: (item: PickerItem) => void;
  onRemove: (id: string) => void;
  /** Extra controls on a selected row — the Primary toggle, for companies. */
  renderSelectedExtra?: (item: PickerItem) => ReactNode;
  createAction?: PickerCreateAction | null;
  /** Shown under the input; the wrapper decides what needs saying. */
  hint?: ReactNode;
  error?: string | null;
  disabled?: boolean;
  /** Message for a search with no results. */
  emptyText: string;
  testId?: string;
}

export default function EntityMultiPicker({
  label,
  required,
  placeholder,
  query,
  onQueryChange,
  options,
  isFetching,
  selected,
  onAdd,
  onRemove,
  renderSelectedExtra,
  createAction,
  hint,
  error,
  disabled,
  emptyText,
  testId,
}: Props) {
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listId = `${baseId}-list`;
  const hintId = `${baseId}-hint`;
  const errorId = `${baseId}-error`;
  const inputRef = useRef<HTMLInputElement>(null);

  const typed = query.trim();
  const open = typed.length > 0;

  // Already-selected records are not offered again — the duplicate is
  // prevented where it would be chosen, not only rejected on submit.
  const available = useMemo(() => {
    const chosen = new Set(selected.map((s) => s.id));
    return options.filter((o) => !chosen.has(o.id));
  }, [options, selected]);

  // Options the keyboard can land on: results, then the create action.
  const rowCount = available.length + (createAction ? 1 : 0);
  const [active, setActive] = useState(-1);
  useEffect(() => setActive(rowCount > 0 ? 0 : -1), [typed, rowCount]);

  const optionId = (i: number) => `${baseId}-opt-${i}`;

  function pick(index: number) {
    if (index < 0) return;
    if (index < available.length) {
      onAdd(available[index]);
      onQueryChange('');
      inputRef.current?.focus();
      return;
    }
    if (createAction && !createAction.isCreating) createAction.onCreate();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => (rowCount === 0 ? -1 : (i + 1) % rowCount));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => (rowCount === 0 ? -1 : (i - 1 + rowCount) % rowCount));
        break;
      case 'Enter':
        // Never let Enter in an open search submit the parent form.
        e.preventDefault();
        pick(active);
        break;
      case 'Escape':
        // Close the list without closing the modal around it.
        e.preventDefault();
        e.stopPropagation();
        onQueryChange('');
        break;
      default:
        break;
    }
  }

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const announcement = !open
    ? ''
    : isFetching
      ? 'Searching…'
      : available.length === 0
        ? emptyText
        : `${available.length} result${available.length === 1 ? '' : 's'} available`;

  return (
    <div data-testid={testId}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required ? ' *' : ''}
      </label>

      {selected.length > 0 && (
        <ul aria-label={`Selected ${label.toLowerCase()}`} className="mb-2 flex flex-col gap-1.5">
          {selected.map((item) => (
            <li
              key={item.id}
              data-testid="picker-selected"
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-gray-900">{item.label}</span>
                {item.detail && (
                  <span className="block truncate text-xs text-gray-500">{item.detail}</span>
                )}
              </span>
              {renderSelectedExtra?.(item)}
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  onRemove(item.id);
                  inputRef.current?.focus();
                }}
                aria-label={`Remove ${item.label}`}
                className="shrink-0 rounded px-1.5 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : undefined}
        autoComplete="off"
        value={query}
        disabled={disabled}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label} results`}
          className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white"
        >
          {available.map((item, i) => (
            <li
              key={item.id}
              id={optionId(i)}
              role="option"
              aria-selected={active === i}
              // mousedown, not click: keeps focus in the input so the list
              // does not collapse before the pick registers.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(i);
              }}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-2 text-sm ${active === i ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
            >
              <span className="text-gray-900">{item.label}</span>
              {item.detail && <span className="ml-2 text-xs text-gray-500">{item.detail}</span>}
            </li>
          ))}

          {createAction && (
            <li
              id={optionId(available.length)}
              role="option"
              aria-selected={active === available.length}
              aria-disabled={createAction.isCreating || undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(available.length);
              }}
              onMouseEnter={() => setActive(available.length)}
              className={`cursor-pointer px-3 py-2 text-sm text-blue-600 ${
                active === available.length ? 'bg-blue-50' : 'hover:bg-blue-50'
              }`}
            >
              {createAction.isCreating ? 'Creating…' : createAction.label}
            </li>
          )}

          {available.length === 0 && !createAction && !isFetching && (
            <li role="presentation" className="px-3 py-2 text-sm text-gray-500">
              {emptyText}
            </li>
          )}
        </ul>
      )}

      <p className="sr-only" aria-live="polite" role="status">
        {announcement}
      </p>

      {hint && (
        <p id={hintId} className="mt-1 text-xs text-amber-700" data-testid="picker-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
